import type { ApiErrorBody, UserSummary } from '@shared/types';
import { productConfig, type FeatureKey } from '@shared/config';

export interface PublicConfig {
  brandName: string;
  billingEnabled: boolean;
  freeDuringBeta: boolean;
  turnstileRequired: boolean;
  turnstileSiteKey: string | null;
}

export interface AccountEntitlements {
  billingEnabled: boolean;
  freeDuringBeta: boolean;
  features: Record<FeatureKey, boolean>;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, body?: Partial<ApiErrorBody['error']>) {
    super(body?.message ?? 'Something went wrong. Please try again.');
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body?.code ?? 'REQUEST_FAILED';
    this.details = body?.details;
  }
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return null;

  try {
    return await response.json();
  } catch {
    return null;
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const method = (init.method ?? 'GET').toUpperCase();
  if (init.body !== undefined && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    headers.set('X-Requested-With', productConfig.brandName);
  }

  const response = await fetch(path, {
    ...init,
    headers,
    credentials: 'same-origin',
  });
  const body = await parseResponseBody(response);

  if (!response.ok) {
    const error =
      body && typeof body === 'object' && 'error' in body
        ? (body as ApiErrorBody).error
        : undefined;
    throw new ApiRequestError(response.status, error);
  }

  return body as T;
}

export const authApi = {
  async me(): Promise<UserSummary | null> {
    try {
      const response = await apiFetch<{ user: UserSummary }>('/api/me');
      return response.user;
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 401) return null;
      throw error;
    }
  },

  async login(input: { email: string; password: string }): Promise<UserSummary> {
    const response = await apiFetch<{ user: UserSummary }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.user;
  },

  async register(input: {
    fullName: string;
    email: string;
    password: string;
    preferredLanguage: 'en' | 'ar';
    turnstileToken?: string;
  }): Promise<UserSummary> {
    const response = await apiFetch<{ user: UserSummary }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.user;
  },

  async logout(): Promise<void> {
    await apiFetch<{ ok: true }>('/api/auth/logout', { method: 'POST' });
  },
};

export async function getPublicConfig(): Promise<PublicConfig> {
  return apiFetch<PublicConfig>('/api/config');
}

export async function getAccountEntitlements(): Promise<AccountEntitlements> {
  return (await apiFetch<{ entitlements: AccountEntitlements }>('/api/account/entitlements'))
    .entitlements;
}

const arabicApiErrors: Record<string, string> = {
  ACCOUNT_DELETE_FAILED: 'تعذّر حذف الحساب. حاول مرة أخرى لاحقًا.',
  AI_DAILY_LIMIT_REACHED:
    'وصلت إلى حد التحسينات بالذكاء الاصطناعي لليوم. عملك المحفوظ ما زال متاحًا.',
  AI_REQUEST_DAILY_LIMIT_REACHED:
    'وصلت إلى حد طلبات الذكاء الاصطناعي لليوم. عملك المحفوظ ما زال متاحًا.',
  AI_EMPTY_RESPONSE: 'لم تُرجع خدمة الذكاء الاصطناعي نتيجة صالحة. حاول مرة أخرى لاحقًا.',
  AI_INVALID_RESPONSE: 'لم تُرجع خدمة الذكاء الاصطناعي نتيجة صالحة. حاول مرة أخرى لاحقًا.',
  AI_UNAVAILABLE: 'ميزة التحسين بالذكاء الاصطناعي غير متاحة مؤقتًا. حاول مرة أخرى لاحقًا.',
  BOT_PROTECTION_UNAVAILABLE: 'إنشاء الحساب غير متاح مؤقتًا. حاول مرة أخرى لاحقًا.',
  CSRF_CHECK_FAILED: 'تعذّر التحقق من الطلب. حدّث الصفحة وحاول مرة أخرى.',
  CV_LIMIT_REACHED: 'وصلت إلى الحد المسموح لعدد السير الذاتية في خطتك الحالية.',
  CV_STORAGE_LIMIT_REACHED: 'وصلت إلى حد مساحة حفظ السير الذاتية في خطتك الحالية.',
  EDIT_CONFLICT: 'تغيّرت هذه السيرة في مكان آخر. حدّث الصفحة ثم راجع تغييراتك قبل الحفظ.',
  FEATURE_NOT_AVAILABLE: 'هذه الميزة غير متاحة في خطتك الحالية.',
  INVALID_CREDENTIALS: 'البريد الإلكتروني أو كلمة المرور غير صحيحين.',
  INVALID_JSON: 'بيانات الطلب غير صالحة. حاول مرة أخرى.',
  INVALID_PASSWORD: 'كلمة المرور غير صحيحة.',
  ORIGIN_NOT_ALLOWED: 'هذا الطلب غير مسموح من هذا المصدر.',
  PROFILE_REQUIRED: 'أكمل ملفك المهني قبل المتابعة.',
  RATE_LIMITED: 'تم إرسال طلبات كثيرة. انتظر قليلًا ثم حاول مرة أخرى.',
  REGISTRATION_FAILED: 'تعذّر إنشاء الحساب بهذه البيانات.',
  TURNSTILE_FAILED: 'تعذّر التحقق من فحص الأمان. حاول مرة أخرى.',
  TURNSTILE_REQUIRED: 'أكمل فحص الأمان للمتابعة.',
  UNAUTHORIZED: 'سجّل الدخول للمتابعة.',
  UNSUPPORTED_MEDIA_TYPE: 'نوع البيانات المرسل غير مدعوم.',
  VALIDATION_ERROR: 'راجع البيانات المظللة وحاول مرة أخرى.',
};

function documentLanguage(): 'en' | 'ar' {
  return typeof document !== 'undefined' && document.documentElement.lang.startsWith('ar')
    ? 'ar'
    : 'en';
}

/**
 * API messages are intentionally localized by stable server error code. This
 * keeps an English server fallback from leaking into an Arabic page.
 */
export function getErrorMessage(
  error: unknown,
  fallback: string,
  language: 'en' | 'ar' = documentLanguage(),
): string {
  if (!(error instanceof ApiRequestError)) return fallback;
  if (language === 'ar') return arabicApiErrors[error.code] ?? fallback;
  return error.message;
}
